import { z } from "@zod/mini";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Config

export const SearchConfigSchema = z.pipe(
  z.object({
    organization: z.object({
      id: z.uuid(),
      name: z.string(),
      strings: z.pipe(
        z.object({
          popular_questions_header: z.string(),
          featured_articles_header: z.string(),
          question_results_header: z.string(),
          article_results_header: z.string(),
          citations_header: z.string(),
          loading_messages: z.array(z.string()),
          loading_confirmation: z.string(),
        }),
        z.transform((strings) => {
          return {
            popularQuestionsHeader: strings.popular_questions_header,
            featuredArticlesHeader: strings.featured_articles_header,
            questionResultsHeader: strings.question_results_header,
            articleResultsHeader: strings.article_results_header,
            citationsHeader: strings.citations_header,
            loadingMessages: strings.loading_messages,
            loadingConfirmation: strings.loading_confirmation,
          };
        }),
      ),
    }),
    conversation_context: z.pipe(
      z.object({
        id: z.uuid(),
        url: z.string(),
        stylesheet: import.meta.env.PROD ? z.url() : z.string(), // localhost is not considered a valid URL
        fallback_search_url: z.nullish(z.url()),
        styles: z.pipe(
          z.object({
            background_color: z.string(),
            foreground_color: z.string(),
            link_color: z.string(),
            link_hover_color: z.string(),
            question_bubble_background_color: z.string(),
            question_bubble_background_hover_color: z.string(),
            question_bubble_foreground_color: z.string(),
            question_bubble_border_color: z.string(),
            font_family: z.nullish(z.string()),
            font_family_fallback: z.string(),
            font_weight: z.number(),
            header_font_family: z.nullish(z.string()),
            header_font_family_fallback: z.string(),
            header_font_weight: z.number(),
          }),
          z.transform((ctx) => {
            return {
              backgroundColor: ctx.background_color,
              foregroundColor: ctx.foreground_color,
              linkColor: ctx.link_color,
              linkHoverColor: ctx.link_hover_color,
              questionBubbleBackgroundColor: ctx.question_bubble_background_color,
              questionBubbleBackgroundHoverColor: ctx.question_bubble_background_hover_color,
              questionBubbleForegroundColor: ctx.question_bubble_foreground_color,
              questionBubbleBorderColor: ctx.question_bubble_border_color,
              fontFamily: ctx.font_family ?? undefined,
              fontFamilyFallback: ctx.font_family_fallback,
              fontWeight: ctx.font_weight,
              headerFontFamily: ctx.header_font_family ?? undefined,
              headerFontFamilyFallback: ctx.header_font_family_fallback,
              headerFontWeight: ctx.header_font_weight,
            };
          }),
        ),
      }),
      z.transform((ctx) => {
        return {
          id: ctx.id,
          url: ctx.url,
          stylesheet: ctx.stylesheet,
          fallbackSearchUrl: ctx.fallback_search_url ?? undefined,
          styles: ctx.styles,
        };
      }),
    ),
    suggested_questions: z.array(
      z.object({
        id: z.nullish(z.number()),
        text: z.string(),
      }),
    ),
    suggested_articles: z.array(
      z.object({
        id: z.nullish(z.number()),
        text: z.string(),
        href: z.url(),
      }),
    ),
    session_token: z.uuid(),
    exclude_from_analytics: z.boolean(),
  }),
  // Map the keys to camelCase
  z.transform((ctx) => {
    return {
      organization: ctx.organization,
      conversationContext: ctx.conversation_context,
      suggestedQuestions: ctx.suggested_questions,
      suggestedArticles: ctx.suggested_articles,
      sessionToken: ctx.session_token,
      excludeFromAnalytics: ctx.exclude_from_analytics,
    };
  }),
);

export type SearchConfig = {
  apiToken: string;
  rootURL: string;
  triggerId: string | undefined;
  organization: {
    id: string;
    name: string;
    strings: {
      popularQuestionsHeader: string;
      featuredArticlesHeader: string;
      questionResultsHeader: string;
      articleResultsHeader: string;
      citationsHeader: string;
      loadingMessages: string[];
      loadingConfirmation: string;
    };
  };
  conversationContext: {
    id: string;
    url: string;
    stylesheet: string;
    fallbackSearchUrl?: string;
    styles: {
      backgroundColor: string;
      foregroundColor: string;
      linkColor: string;
      linkHoverColor: string;
      questionBubbleBackgroundColor: string;
      questionBubbleBackgroundHoverColor: string;
      questionBubbleForegroundColor: string;
      questionBubbleBorderColor: string;
      fontFamily?: string;
      fontFamilyFallback: string;
      fontWeight: number;
      headerFontFamily?: string;
      headerFontFamilyFallback: string;
      headerFontWeight: number;
    };
  };
  suggestedQuestions: {
    id?: number | null | undefined;
    text: string;
  }[];
  suggestedArticles: {
    id?: number | null | undefined;
    text: string;
    href: string;
  }[];
  sessionToken: string;
  excludeFromAnalytics: boolean;
};
export type Organization = SearchConfig["organization"];
export type ConversationContext = SearchConfig["conversationContext"];

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Question

export const QuestionSchema = z.object({
  id: z.number(),
  rank: z.number(),
  text: z.string(),
});
export type Question = {
  id?: number | null | undefined;
  rank: number;
  text: string;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Article

export const ArticleSchema = z.pipe(
  z.object({
    id: z.number(),
    rank: z.number(),
    text: z.string(),
    href: z.nullish(z.string()),
  }),
  z.transform((data) => ({
    ...data,
    href: data.href ?? undefined, // Remove null values
  })),
);
export type Article = {
  id?: number | null | undefined;
  rank: number;
  text: string;
  href?: string;
};

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Conversations Response

// Annotations

export enum AnnotationType {
  Suggestion = "suggestion",
  Citation = "citation",
  RelatedQuestion = "related_question",
}

export const AnnotationSchema = z.pipe(
  z.object({
    type: z.enum(Object.values(AnnotationType)),
    rank: z.number(),
    id: z.number(),
    text: z.string(),
    href: z.nullish(z.string()),
  }),
  z.transform((data) => ({
    ...data,
    href: data.href ?? undefined, // Convert null to undefined
  })),
);
export type Annotation = {
  type: AnnotationType;
  rank: number;
  id: number;
  text: string;
  href?: string;
};

// Conversation Turns

export enum ConversationTurnRouting {
  Exact = "exact",
  Near = "near",
  Suggestion = "suggestion",
  NoMatch = "no_match",
  Keyword = "keyword",
  Conversational = "conversational",
  Irrelevant = "irrelevant",
  Unknown = "unknown",
}

export const ConversationTurnSchema = z.pipe(
  z.object({
    created_at: z.nullish(z.number()),
    rank: z.nullish(z.number()),
    routing: z.nullish(z.enum(Object.values(ConversationTurnRouting))),
    content: z.nullish(
      z.object({
        value: z.nullish(z.string()),
        annotations: z.nullish(z.array(AnnotationSchema)),
      }),
    ),
  }),
  z.transform((data) => ({
    createdAt: data.created_at || undefined,
    rank: data.rank || undefined,
    routing: data.routing || undefined,
    content: data.content
      ? {
        value: data.content.value || undefined,
        annotations: data.content.annotations || undefined,
      }
      : undefined,
  })),
);
export type ConversationTurn = {
  createdAt?: number;
  rank?: number;
  routing?: ConversationTurnRouting;
  content?: {
    value?: string;
    annotations?: Annotation[];
  };
};

// Conversations

export const ConversationSchema = z.object({
  id: z.uuid(),
});
export type Conversation = {
  id: string;
};
